/**
 * Auto contrast guardrail (CLAUDE.md §5): when a merchant picks a primary
 * colour we choose the text colour that sits on top of it, so nobody ships a
 * storefront with white text on a pastel pink "Buy" button.
 *
 * WCAG 2.1 relative luminance + contrast ratio. 4.5:1 is the AA threshold for
 * body text; buttons carry large-ish text so 3:1 would pass, but we aim for
 * 4.5 and only fall back when neither black nor white reaches it.
 */

export interface Rgb {
  r: number;
  g: number;
  b: number;
}

export function parseHex(hex: string): Rgb | null {
  const clean = hex.trim().replace(/^#/, '');
  const expanded =
    clean.length === 3
      ? clean
          .split('')
          .map((c) => c + c)
          .join('')
      : clean;
  if (!/^[0-9a-fA-F]{6}$/.test(expanded)) return null;
  return {
    r: Number.parseInt(expanded.slice(0, 2), 16),
    g: Number.parseInt(expanded.slice(2, 4), 16),
    b: Number.parseInt(expanded.slice(4, 6), 16),
  };
}

export function toHex({ r, g, b }: Rgb): string {
  const part = (n: number) => Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, '0');
  return `#${part(r)}${part(g)}${part(b)}`;
}

function channelLuminance(channel: number): number {
  const c = channel / 255;
  return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
}

export function relativeLuminance(color: Rgb): number {
  return (
    0.2126 * channelLuminance(color.r) +
    0.7152 * channelLuminance(color.g) +
    0.0722 * channelLuminance(color.b)
  );
}

export function contrastRatio(a: Rgb, b: Rgb): number {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  const lighter = Math.max(la, lb);
  const darker = Math.min(la, lb);
  return (lighter + 0.05) / (darker + 0.05);
}

const WHITE: Rgb = { r: 255, g: 255, b: 255 };
const BLACK: Rgb = { r: 17, g: 24, b: 39 }; // slate-900, softer than pure black

/**
 * Pick the readable foreground for a background. Returns the hex of whichever
 * of near-black / white has the better contrast.
 */
export function contrastingText(backgroundHex: string): string {
  const bg = parseHex(backgroundHex);
  if (!bg) return '#ffffff';
  return contrastRatio(bg, WHITE) >= contrastRatio(bg, BLACK) ? '#ffffff' : toHex(BLACK);
}

export interface ContrastCheck {
  ratio: number;
  passesAA: boolean;
  passesLargeAA: boolean;
  suggestion: string;
}

/** Used by the admin colour picker to warn (and auto-fix) in realtime. */
export function checkPrimary(primaryHex: string): ContrastCheck {
  const fallback: ContrastCheck = {
    ratio: 0,
    passesAA: false,
    passesLargeAA: false,
    suggestion: '#ffffff',
  };
  const bg = parseHex(primaryHex);
  if (!bg) return fallback;
  const suggestion = contrastingText(primaryHex);
  const fg = parseHex(suggestion) ?? WHITE;
  const ratio = contrastRatio(bg, fg);
  return {
    ratio: Math.round(ratio * 100) / 100,
    passesAA: ratio >= 4.5,
    passesLargeAA: ratio >= 3,
    suggestion,
  };
}

/** Darken/lighten for hover and border shades derived from the primary. */
export function shade(hex: string, amount: number): string {
  const rgb = parseHex(hex);
  if (!rgb) return hex;
  const mix = amount < 0 ? 0 : 255;
  const t = Math.abs(amount);
  return toHex({
    r: rgb.r + (mix - rgb.r) * t,
    g: rgb.g + (mix - rgb.g) * t,
    b: rgb.b + (mix - rgb.b) * t,
  });
}
